'use client'

import type { EvmChainId } from 'sushi/evm'
import { useMyTokens } from './hooks/use-my-tokens'
import { useSearchTokens } from './hooks/use-search-tokens'
import { TokenSelectorChipBar } from './token-lists/token-selector-chip-bar'
import { TokenSelectorCustomList } from './token-lists/token-selector-custom-list'
import { TokenSelectorMyTokens } from './token-lists/token-selector-my-tokens'
import { TokenSelectorSearch } from './token-lists/token-selector-search'

interface TokenSelectorStates<TChainId extends EvmChainId> {
  selected: CurrencyFor<TChainId> | undefined
  chainId: TChainId
  account?: AddressFor<TChainId>
  onSelect(currency: CurrencyFor<TChainId>): void
  onShowInfo(currency: CurrencyFor<TChainId> | false): void
  currencies?: CurrencyFor<TChainId, { approved?: boolean }>[]
  includeNative?: boolean
  hidePinnedTokens?: boolean
  search?: string
}

export function TokenSelectorStates<TChainId extends EvmChainId>({
  selected,
  chainId,
  account,
  onSelect,
  onShowInfo,
  currencies,
  includeNative,
  hidePinnedTokens,
  search,
}: TokenSelectorStates<TChainId>) {
  // Ensure that the user's tokens are loaded
  useMyTokens({
    chainId,
    account,
    includeNative,
  })

  // Ensure that the search list is loaded
  useSearchTokens({
    chainId,
    search: '',
  })

  if (currencies) {
    return (
      <TokenSelectorCustomList
        chainId={chainId}
        account={account}
        currencies={currencies}
        onSelect={onSelect}
        selected={selected}
        search={search}
        includeNative={includeNative}
        onShowInfo={onShowInfo}
      />
    )
  }

  if (search) {
    return (
      <TokenSelectorSearch
        chainId={chainId}
        onSelect={onSelect}
        onShowInfo={onShowInfo}
        search={search}
        selected={selected}
      />
    )
  }

  return (
    <>
      <TokenSelectorChipBar
        chainId={chainId}
        onSelect={onSelect}
        includeNative={includeNative}
        showPinnedTokens={!hidePinnedTokens}
      />

      {account ? (
        <TokenSelectorMyTokens
          chainId={chainId}
          onSelect={onSelect}
          onShowInfo={onShowInfo}
          selected={selected}
          includeNative={includeNative}
        />
      ) : null}

      <TokenSelectorSearch
        chainId={chainId}
        onSelect={onSelect}
        onShowInfo={onShowInfo}
        selected={selected}
        search=""
      />
    </>
  )
}
